/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
'use client';

import { Collapse, Expand, Sort } from '@ahamove/icons/order';
import {
  apiRoutes,
  type OptimizedRoutesResponse,
  type ShortestRoute,
} from '@ahm/api-wrappers-core';
import { detectLatestPickupOrder } from '@ahm/react-map';
import { useLocationStore } from '@ahm/react-map/store';
import {
  Box,
  Button,
  Card,
  Group,
  LoadingOverlay,
  Text,
  Title,
  type CardProps,
} from '@mantine/core';
import { openContextModal } from '@mantine/modals';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { Fragment, useEffect, useMemo, useState, type MouseEvent } from 'react';
import classes from './path-btn.module.css';
import PathDnd from './path-dnd';
import PathItem from './path-item';
import SwapIcon from '@/assets/icons/swap.svg';
import { ModalName } from '@/components/modals';
import { ROUTES } from '@/config/router';
import { QuickAccessAIChatCard } from '@/features/AI';
import { useDetectBanHour } from '@/hooks';
import {
  ActionIndexPath,
  DEFAULT_MAX_STOP_HM,
  DEFAULT_MAX_STOP_INTERCITY,
} from '@/lib/constants';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import {
  getMaxStopPointsConfig,
  getServiceType,
  routeOptimization_getTheShortestRoute,
} from '@/lib/utils';
import { usePathname } from '@/navigation';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

type Props = Omit<CardProps, 'children'> & {
  disableHeader?: boolean;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export default function BookingPaths({
  disableHeader = false,
  ...props
}: Props) {
  const pathname = usePathname();
  const tBooking = useTranslations('booking');
  const tCommon = useTranslations('common');
  const isMobile = useIsMobile();

  const { isBikeService } = useMemo(() => getServiceType(pathname), [pathname]);

  const {
    selectedService,
    estimatedData,
    isLoadingStore,
    setPrevSelectedService,
  } = useBookingStore((state) => state);
  const paths = useLocationStore.use.paths();
  const setPaths = useLocationStore.use.setPaths();
  const setShortestRoute = useLocationStore.use.setShortestRoute();
  const detectLocation = useLocationStore.use.detectLocation();
  const { checkServiceInBanFn } = useDetectBanHour();

  const NUMBER_COLLAPSED = 4;
  const [collapsed, setCollapsed] = useState(true);
  const [dndMode, setDndMode] = useState(false);
  const [pickupPointLoading, setPickupPointLoading] = useState<boolean>(false);
  const updatePathByIndex = useLocationStore.use.updatePathByIndex();

  const pickup = useMemo(() => {
    return paths ? paths[0] : undefined;
  }, [paths]);

  useEffect(() => {
    if (!pickup && !pickupPointLoading) {
      setPickupPointLoading(true);
      detectLatestPickupOrder()
        .then((res) => {
          if (res) updatePathByIndex(res, ActionIndexPath.Pickup);
        })
        .catch((err) => console.error('err detectLatestPickupOrder', err))
        .finally(() => {
          setPickupPointLoading(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const optimizeParamString = useMemo(() => {
    let stringParam = '';
    if (paths && paths?.length > 2) {
      paths?.forEach((item, index) => {
        stringParam += `${item.lng},${item.lat}${index + 1 === paths.length ? '' : ';'}`;
      });
    }
    return stringParam;
  }, [paths]);

  // const { data: optimizedData } = useGetOptimizeRoutes(optimizeParamString, {
  //   enabled: !!optimizeParamString,
  // });

  const queryClient = useQueryClient();

  const optimizedData: OptimizedRoutesResponse | undefined =
    queryClient.getQueryData([apiRoutes.osrm.driving, optimizeParamString]);

  useEffect(() => {
    if (pathname === ROUTES.nationwide && paths.length > 2) {
      const newPaths = paths.slice(0, 2);
      setPaths(newPaths);
    }
  }, [pathname, paths, setPaths]);

  useEffect(() => {
    const route: ShortestRoute | undefined =
      routeOptimization_getTheShortestRoute(optimizedData ?? null, paths);

    if (route) {
      setShortestRoute(route);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optimizedData, paths]);

  const handleDndMode = () => {
    if (isMobile) {
      openContextModal({
        id: ModalName.DndPath,
        modal: ModalName.DndPath,
        innerProps: {},
        fullScreen: true,
      });
    } else {
      setDndMode(true);
    }
  };

  const stopPoints = useMemo(() => {
    if (!paths) return undefined;
    return paths.slice(1);
  }, [paths]);

  const maxStopPoints = useMemo(() => {
    if (!selectedService) {
      if (pathname === ROUTES.nationwide) return DEFAULT_MAX_STOP_INTERCITY;
      if (pathname === ROUTES.houseMoving) return DEFAULT_MAX_STOP_HM;
    }
    return getMaxStopPointsConfig(selectedService);
  }, [selectedService, pathname]);

  const handleClose = () => {
    setDndMode(false);
  };

  const handleSwapPath = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (paths?.length === 2) {
      if (selectedService) {
        if (!checkServiceInBanFn(selectedService)) {
          setPrevSelectedService(selectedService);
        } else {
          setPrevSelectedService(null);
        }
      }
      const newPaths = [paths[1], paths[0]];
      newPaths[0].cod = undefined;
      newPaths[1].cod = undefined;
      newPaths[0].item_value = undefined;
      newPaths[1].item_value = undefined;
      setPaths(newPaths);
    }
  };

  const handleSubmitDnd = (sortedIndexes: number[]) => {
    if (!sortedIndexes) {
      handleClose();
      return;
    }

    const newPaths = sortedIndexes.map((idx) => paths[idx]);

    if (sortedIndexes[0] !== 0) {
      newPaths.forEach((path) => (path.cod = undefined));
    }

    setPaths(newPaths);
    handleClose();
  };

  useEffect(() => {
    if (!paths || paths?.length === 0) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      detectLocation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Card
      pos="relative"
      style={{
        overflow: 'unset',
      }}
      className={!dndMode ? classes.root : ''}
      shadow="0px 2px 4px 0px rgba(0, 0, 0, 0.10)"
      radius={12}
      padding={0}
      pb={isBikeService ? 0 : 'xs'}
      {...props}
    >
      <LoadingOverlay
        visible={isLoadingStore}
        loaderProps={{ children: <Fragment /> }}
        styles={{
          overlay: {
            borderRadius: 12,
          },
        }}
      />
      {dndMode && paths && paths.length > 2 ? (
        <PathDnd
          opened={dndMode}
          handleClose={handleClose}
          onSubmit={handleSubmitDnd}
        />
      ) : (
        <>
          {disableHeader ? null : (
            <Group justify="space-between" wrap="nowrap" p="sm">
              <Title order={4}>
                {tBooking('location.delivery_route')}{' '}
                {/* <Space display="inline-block" w={8} /> */}
                {estimatedData?.data?.distance ? (
                  <Text size="md" fw={700} c="neutral.5" component="span">
                    {' - '} {estimatedData.data.distance}km
                  </Text>
                ) : undefined}
              </Title>
              {paths && paths.length > 1 ? (
                paths.length > 2 ? (
                  <Button
                    variant="subtle"
                    color="secondary.7"
                    size="compact-sm"
                    fw={700}
                    flex="none"
                    leftSection={<Sort height={16} width={16} />}
                    onClick={handleDndMode}
                  >
                    {tCommon('arrange')}
                  </Button>
                ) : (
                  <Button
                    variant="subtle"
                    color="secondary.7"
                    styles={{
                      label: { color: 'var(--mantine-color-neutral-7)' },
                    }}
                    size={isMobile ? 'compact-sm' : 'compact-md'}
                    fw={700}
                    flex="none"
                    leftSection={<SwapIcon />}
                    onClick={handleSwapPath}
                  >
                    {tCommon('swap')}
                  </Button>
                )
              ) : (
                <Fragment />
              )}
            </Group>
          )}

          <PathItem path={pickup} pathIndex={0} />

          {stopPoints && stopPoints.length > 0 ? (
            <>
              {stopPoints.length > NUMBER_COLLAPSED ? (
                collapsed ? (
                  <Box py={4} px={34}>
                    <Button
                      size="xs"
                      variant="subtle"
                      color="secondary.7"
                      rightSection={<Expand height={16} width={16} />}
                      styles={{ label: { textTransform: 'lowercase' } }}
                      onClick={() => setCollapsed(false)}
                    >
                      {stopPoints.length - 1}{' '}
                      {tBooking('location.delivery_points')}
                    </Button>
                  </Box>
                ) : (
                  <Box py={4} px={34}>
                    <Button
                      size="xs"
                      variant="subtle"
                      color="secondary.7"
                      rightSection={<Collapse height={16} width={16} />}
                      onClick={() => setCollapsed(true)}
                    >
                      {tCommon('collapse')}
                    </Button>
                  </Box>
                )
              ) : undefined}
              {(collapsed && stopPoints.length > NUMBER_COLLAPSED
                ? [stopPoints[stopPoints.length - 1]]
                : stopPoints
              ).map((path, idx) => (
                <PathItem
                  key={idx + 1}
                  pathIndex={
                    collapsed && stopPoints.length > NUMBER_COLLAPSED
                      ? stopPoints.length
                      : idx + 1
                  }
                  path={path}
                />
              ))}

              {stopPoints.length < maxStopPoints ? (
                <PathItem pathIndex={ActionIndexPath.AddMoreBtn} />
              ) : null}
            </>
          ) : (
            <PathItem pathIndex={ActionIndexPath.ShowInput} />
          )}
        </>
      )}
      <QuickAccessAIChatCard />
    </Card>
  );
}
